import React, { useState, useEffect } from "react";
import { Modal, FormActions, Input, IconsLarge } from "../../shared";
import { bankAccountsApi } from "../../../services";
import type { BankAccountAttributes } from "../../../types";

interface FormData {
  name: string;
  openingDate: string;
  initialBalance: string;
}

interface CreateBankAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  editingAccount?: BankAccountAttributes | null;
}

export const CreateBankAccountModal: React.FC<CreateBankAccountModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  editingAccount,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    openingDate: "",
    initialBalance: "0",
  });

  useEffect(() => {
    if (editingAccount) {
      setFormData({
        name: editingAccount.name,
        openingDate: editingAccount.openingDate ?? "",
        initialBalance: String(editingAccount.initialBalance),
      });
    } else {
      setFormData({
        name: "",
        openingDate: "",
        initialBalance: "0",
      });
    }
    setError(null);
  }, [editingAccount, isOpen]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError("Nome da conta é obrigatório");
      return;
    }
    const initialBalance = parseFloat(formData.initialBalance);
    if (Number.isNaN(initialBalance)) {
      setError("Saldo inicial deve ser um número");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingAccount) {
        const response = await bankAccountsApi.update(editingAccount.id, {
          name: formData.name.trim(),
          openingDate: formData.openingDate || null,
          initialBalance,
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao atualizar conta");
        }
      } else {
        const response = await bankAccountsApi.create({
          name: formData.name.trim(),
          companyId,
          openingDate: formData.openingDate || null,
          initialBalance,
        });
        if (response.success) {
          onSuccess();
          onClose();
        } else {
          setError(response.error ?? "Falha ao criar conta");
        }
      }
    } catch {
      setError(editingAccount ? "Falha ao atualizar conta" : "Falha ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", openingDate: "", initialBalance: "0" });
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingAccount ? "Editar Conta" : "Nova Conta"}
      icon={<IconsLarge.BankAccount />}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 mb-6">
          <span className="text-xl">⚠️</span>
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      <div className="space-y-6">
        <Input
          label="Nome da conta"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          placeholder="Ex: Conta Corrente, Poupança..."
          disabled={loading}
          required
        />

        <Input
          label="Data de abertura"
          type="date"
          value={formData.openingDate}
          onChange={(e) => setFormData({ ...formData, openingDate: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          disabled={loading}
        />

        <Input
          label="Saldo inicial (R$)"
          type="number"
          value={formData.initialBalance}
          onChange={(e) => setFormData({ ...formData, initialBalance: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
            if (e.key === "Escape") handleClose();
          }}
          step={1.00}
          disabled={loading}
          placeholder="0.00"
        />

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          isEditing={!!editingAccount}
          submitType="button"
          submitText={editingAccount ? "Atualizar conta" : "Criar conta"}
        />
      </div>
    </Modal>
  );
};
